// @file Composable for whiteboard page handlers
import { captureException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { useSurfaceCurrentUserStore } from '@@/pinia/surface_current_user'
import { cloneDeep } from 'es-toolkit'
import { storeToRefs } from 'pinia'
import type { Editor, TLPage } from 'tldraw'

export const useWhiteboardPageHandlers = (
  editor: Editor,
): {
  updatePageAttribution: (page: TLPage) => TLPage
  registerSideEffects: () => void
} => {
  const { currentUser } = storeToRefs(useSurfaceCurrentUserStore())

  // #region Helpers
  const isPageAttributionPopulated = (meta: TLPage['meta']): boolean => {
    return meta.lastModifiedBy != null && meta.lastModifiedAt != null
  }
  // #endregion

  // #region PAGE (frame) SIDE EFFECTS
  const updatePageAttribution = (page: TLPage): TLPage => {
    const clonedPage = cloneDeep(page)
    if (page === undefined || clonedPage === undefined) {
      captureException(new Error('Page is undefined on before change'), { tags: { isTldrawError: true } })
      return page
    }
    clonedPage.meta = clonedPage.meta ?? {}
    clonedPage.meta.lastModifiedAt = Date.now()
    clonedPage.meta.lastModifiedBy = currentUser.value.id
    return clonedPage
  }

  const registerSideEffects = (): void => {
    editor.sideEffects.registerBeforeCreateHandler('page', (page, source) => {
      if (source !== 'user') return page

      // Assign attribution when page is created. Page attribution can already be populated
      // if the page is duplicated (in which case we keep its original meta).
      if (!isPageAttributionPopulated(page.meta)) {
        const clonedPage = cloneDeep(page)
        clonedPage.meta = {
          // Page meta can have other attributies like `backgroundType`. We need to keep them.
          ...clonedPage.meta,
          lastModifiedAt: Date.now(),
          lastModifiedBy: currentUser.value.id,
          useThemeFrameBackground: isAppUsing('sandboxThemeBackgrounds'),
        }
        return clonedPage
      }

      return page
    })

    editor.sideEffects.registerBeforeChangeHandler('page', (_prev, next, source) => {
      if (source !== 'user') return next
      return updatePageAttribution(next)
    })
  }
  // #endregion

  return { updatePageAttribution, registerSideEffects }
}
